import React from 'react';
import { ChevronLeft, X, Clapperboard } from 'lucide-react';
import Tooltip from '../ui/Tooltip';
import { SIDE_PANE_RAIL, SIDE_PANE_WIDTH } from '../../constants/layout';

interface Props {
  open: boolean;
  title: string;
  /** Omit to remove the close affordance — on the workstation the Studio IS the dashboard, so there is
   *  nothing behind it to close back to. Mobile always passes one (the viewer list sits behind). */
  onClose?: () => void;
  /** Reserve the left pane's CURRENT width beside the Studio: 'open' = the explorer, 'rail' = the collapsed
   *  strip (3rem) so its expand button stays reachable — covering it locked the pane shut. */
  leftRail?: 'open' | 'rail';
  /** The left pane's LIVE width — the shared token until the creator drags its border, then their width. */
  leftWidth?: string;
  /** The Generate / action bar pinned to the bottom. */
  footer?: React.ReactNode;
  /**
   * The production's own controls, rendered ON the header line instead of a second band below it (the stage
   * stepper). When present it REPLACES the "Video Studio" wordmark: the creator already knows which app they
   * are in, and a title bar that says nothing but its own name is a line of canvas height spent on nothing.
   * Absent (the launchpad, before there is a production) the wordmark stands in.
   */
  toolbar?: React.ReactNode;
  /** Controlled maximize state (so the content can switch to the full-screen canvas view). Uncontrolled if omitted. */
  /** Full-screen. There is no side-panel mode any more; defaults true. */
  maximized?: boolean;
  /**
   * Reserve the right rail for the chat co-pilot when EXPANDED (desktop only). Set this wherever a chat is
   * actually mounted behind the Studio; leave it off on the launchpad, where there is nothing to split with and
   * a reserved strip would just show empty background.
   */
  chatRail?: boolean;
  /** Whether the chat pane is currently slid open — folded, it still leaves its 3rem rail behind. */
  chatRailOpen?: boolean;
  /** The right pane's LIVE width, same deal as `leftWidth`. */
  chatWidth?: string;
  /** False while a pane border is being dragged: the canvas must track the pointer, not trail 200ms behind it. */
  smooth?: boolean;
  children: React.ReactNode;
}

/**
 * StudioWorkspace — the Video Studio's workflow shell. ONE surface, three fits:
 *   • Desktop full    — a (near) full-screen creation canvas (the DEFAULT — Studio is a destination, not a widget).
 *   • Desktop docked  — restored to a right-edge panel so the chat is visible alongside, for anyone who wants it.
 *   • Mobile page     — a full-screen page with a back arrow (SheetOrPage asPage philosophy).
 *
 * Presentational only: it owns the frame (header, dock/full toggle, scroll body, footer bar); the Studio steps
 * are passed as children. Monochrome + Lucide, consistent with the app's design language.
 */
const StudioWorkspace: React.FC<Props> = ({ open, title, onClose, footer, toolbar, maximized: maximizedProp, leftRail, leftWidth, chatRail, chatRailOpen = true, chatWidth, smooth = true, children }) => {
  // Always true in practice — the Studio has no side-panel mode. Kept as a prop so the shell stays reusable.
  const maximized = maximizedProp ?? true;

  // Esc used to restore from full-screen. With no side-panel mode there is nothing to restore TO, and the
  // canvas needs Esc for its own dismissals (the context menu, the transition chooser) — a global handler
  // here would swallow them.

  if (!open) return null;

  // Mobile = full-screen page; desktop = docked right panel (or expanded). dvh (not h-screen) so mobile
  // Safari's toolbar doesn't hide the footer. Width/position switch at md:.
  //
  // EXPANDED is a SPLIT, not a full-bleed takeover: Studio fills the main region and leaves the right rail for
  // chat, so the crew stays visible and assist-while-you-build works. Studio is the destination; chat is the
  // co-pilot beside it. On the launchpad (no chat to split with) the caller passes no rail and we fill the screen.
  //
  // WHAT THE CANVAS GIVES UP, on each side, read from the SAME tokens the panes themselves use so the three
  // numbers can never disagree again (they were declared in four unrelated places before slice E). A folded
  // pane still keeps its 3rem rail — its expand control and its one status signal live there.
  // `leftWidth` is authoritative whenever the host supplies it, in BOTH states: only the host knows that the
  // reservation also has to cover the activity bar, which is not part of either pane.
  const leftReserve = !leftRail ? '0px'
    : leftWidth || (leftRail === 'open' ? SIDE_PANE_WIDTH : SIDE_PANE_RAIL);
  const rightReserve = !chatRail ? '0px' : chatRailOpen ? (chatWidth || SIDE_PANE_WIDTH) : SIDE_PANE_RAIL;

  const frame = [
    'fixed z-40 flex flex-col bg-white dark:bg-secondary-900',
    'inset-x-0 top-0 h-[100dvh]', // mobile: full page
    smooth ? 'md:transition-[right,left] md:duration-200 md:ease-out' : '', // reclaim in step with the slide
    maximized
      ? 'md:inset-y-0 md:h-full md:left-[var(--studio-left)] md:right-[var(--studio-rail)]'
      : 'md:inset-y-0 md:right-0 md:left-auto md:h-full md:w-[480px] md:border-l md:border-secondary-200 md:dark:border-secondary-800 md:shadow-xl',
  ].join(' ');

  return (
    <div
      className={frame}
      data-testid="studio-workspace"
      style={{ ['--studio-left' as string]: leftReserve, ['--studio-rail' as string]: rightReserve }}
    >
      {/* ONE header line (owner 2026-08-23, screenshot93). The journey used to sit in a second band below this
          bar and a third line of prose below that; all of it now shares this row, which is the only chrome
          between the creator and the canvas. */}
      <div className="flex items-center gap-2 border-b border-secondary-200 px-3 py-1.5 dark:border-secondary-800 md:px-4">
        {/* Mobile back arrow; desktop shows the Studio glyph */}
        <button onClick={onClose} aria-label="Back" className="shrink-0 md:hidden text-secondary-500 hover:text-secondary-900 dark:hover:text-white">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <Clapperboard className="hidden md:block h-4 w-4 text-secondary-700 dark:text-secondary-300 shrink-0" />
        {toolbar
          ? <div className="min-w-0 flex-1">{toolbar}</div>
          : <h2 className="min-w-0 flex-1 text-sm font-semibold text-secondary-900 dark:text-white truncate">{title}</h2>}
        <div className="flex shrink-0 items-center gap-1">
          {/* NO CHAT TOGGLE HERE (owner 2026-08-21): "that toggle is not on video studio pane, but the right
              pane". A control that folds the chat away belongs ON the chat, mirroring the explorer's own
              toggle — and keeping it here also made it a bespoke button, visibly smaller than the left
              pane's. Both toggles now come from the shared PaneChrome, identical but for direction. */}
          {/* NO SHRINK CONTROL (owner 2026-08-14): "Video Studio is only expanded view — either it's full
              screen or not opened". The side-panel mode is gone, so a control that shrinks into it would lead
              nowhere. */}
          {/* No X when there is nowhere to close TO — on the workstation the Studio is the dashboard. */}
          {onClose && (
            <Tooltip content="Close">
              <button onClick={onClose} aria-label="Close" className="hidden md:inline-flex p-1 text-secondary-400 hover:text-secondary-700 dark:hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </Tooltip>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">{children}</div>

      {footer && (
        <div className="border-t border-secondary-200 dark:border-secondary-800 px-4 py-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] md:pb-3">
          {footer}
        </div>
      )}
    </div>
  );
};

export default StudioWorkspace;
